import { useState, useRef } from "react";
import PropTypes from "prop-types";

import { fileTypeConfigs, strCode } from "../../consts";
import { notificationService, t } from "../../services";

import WrapperContainer from "../Containers/WrapperContainer";
import FlexContainers from "../Containers/FlexContainers";
import { Paragraph2 } from "../Text/Paragraphs";
import { TextButton } from "../Buttons/styled";

import { UploadedFile } from "./components/UploadedFile";

import {
  DragFileWrapper,
  DrapAndDropWrapper,
  InputFileUploadLabel,
  WarningMessage
} from "./styled";
import { InputContainer, InputTitleWrapper } from "../Input/styled";

const maxFileSizeMb = 10;
const maxFileSize = maxFileSizeMb * 1024 * 1024; // 10 Mb

const FileInput = ({
  id,
  title,
  file,
  setFile,
  fileType,
  defaultFileName,
  warningMsg,
  fileFomatErrorMsg,
  disabled,
  hasError,
  onRemoveFile
}) => {
  const [dragActive, setDragActive] = useState(false);
  const [error, setError] = useState(false);

  const fileTypeConfig = fileType ? fileTypeConfigs[fileType] : null;

  const inputRef = useRef(null);

  const handleUploadFile = (file) => {
    const extension = file?.type;
    const size = file?.size;

    if (fileTypeConfig && !fileTypeConfig?.extensions.includes(extension)) {
      setError(true);
      notificationService.showError({
        title: t(strCode.FILE_FORMAT_ERROR_TITLE),
        message: t(fileFomatErrorMsg ? fileFomatErrorMsg : strCode.FILE_FORMAT_ERROR_MESSAGE)
      });
    } else if (size > maxFileSize) {
      setError(true);
      notificationService.showError({
        title: t(strCode.FILE_SIZE_ERROR_TITLE, { size: `${maxFileSizeMb} Mb` }),
        message: t(strCode.FILE_SIZE_ERROR_MESSAGE)
      });
    } else {
      setError(false);
      setFile(file);
    }
  };

  const handleDrag = (e) => {
    e.preventDefault();
    e.stopPropagation();
    if (e.type === "dragenter" || e.type === "dragover") {
      setDragActive(true);
    } else if (e.type === "dragleave") {
      setDragActive(false);
    }
  };

  const handleDrop = function (e) {
    e.preventDefault();
    e.stopPropagation();
    setDragActive(false);
    if (e.dataTransfer.files && e.dataTransfer.files[0]) {
      handleUploadFile(e.dataTransfer.files[0]);
    }
  };

  const handleChange = (e) => {
    e.preventDefault();
    if (e.target.files && e.target.files[0]) {
      handleUploadFile(e.target.files[0]);
      e.target.value = "";
    }
  };

  const onButtonClick = (e) => {
    e?.preventDefault();
    inputRef.current.click();
  };

  return (
    <InputContainer
      data-testid="input-container"
      className={disabled ? "disabled" : hasError || error ? "error" : ""}>
      <WrapperContainer Container={FlexContainers.FlexRowBetween} width="100%">
        {title && (
          <InputTitleWrapper>
            <label htmlFor={id}>{title}</label>
          </InputTitleWrapper>
        )}

        {warningMsg && (
          <WrapperContainer Container={FlexContainers.FlexRowEnd}>
            <InputTitleWrapper>
              <WrapperContainer width="max-content">
                <WarningMessage>{warningMsg}</WarningMessage>
              </WrapperContainer>
            </InputTitleWrapper>
          </WrapperContainer>
        )}
      </WrapperContainer>

      {file ? (
        <UploadedFile
          file={file}
          defaultFileName={defaultFileName}
          disabled={disabled}
          onRemoveFile={onRemoveFile ? onRemoveFile : () => setFile(null)}
        />
      ) : (
        <WrapperContainer Container={FlexContainers.FlexColumnStart} width="100%">
          <DrapAndDropWrapper
            onDragEnter={handleDrag}
            onSubmit={(e) => e.preventDefault()}
            disabled={disabled}>
            <input
              data-testid="input-file-upload"
              ref={inputRef}
              type="file"
              accept={fileTypeConfig && fileTypeConfig.accept}
              id="input-file-upload"
              onChange={(e) => handleChange(e)}
            />
            <InputFileUploadLabel
              id="label-file-upload"
              htmlFor="input-file-upload"
              onClick={onButtonClick}
              dragActive={dragActive}
              hasError={hasError || error}>
              <WrapperContainer Container={FlexContainers.FlexRowCenter} gap={3}>
                <Paragraph2>{t(strCode.DRAG_AND_DROP_FILE_INFO)}</Paragraph2>
                <TextButton>
                  <Paragraph2>{t(strCode.CHOOSE_FILE_BTN)}</Paragraph2>
                </TextButton>
              </WrapperContainer>
            </InputFileUploadLabel>

            {dragActive && (
              <DragFileWrapper
                onDragEnter={handleDrag}
                onDragLeave={handleDrag}
                onDragOver={handleDrag}
                onDrop={handleDrop}></DragFileWrapper>
            )}
          </DrapAndDropWrapper>
        </WrapperContainer>
      )}
    </InputContainer>
  );
};

FileInput.propTypes = {
  id: PropTypes.string.isRequired,
  title: PropTypes.string,
  file: PropTypes.object,
  fileType: PropTypes.string,
  defaultFileName: PropTypes.string,
  setFile: PropTypes.func.isRequired,
  warningMsg: PropTypes.string,
  fileFomatErrorMsg: PropTypes.string,
  disabled: PropTypes.bool,
  hasError: PropTypes.bool,
  onRemoveFile: PropTypes.func
};

export default FileInput;
